/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ErrorOutline,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import T from 'i18n';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectCanUseBetaOptions,
  selectCaseClaimNo,
  selectCaseDataRoomSettings,
  selectCaseShortName,
  selectConfidentialityRing,
  selectCurrentCaseId,
  selectCurrentFolder,
  selectCurrentUserConfidentialityRing,
  selectUseContainerBasedMerge,
} from 'common/selectors';
import { useForm } from 'react-hook-form';
import * as actions from '../../redux/actions';
import { useFetchNotifications } from 'features/case/redux/fetchNotifications';
import moment from 'moment';
import { MultiSelectForm, SelectForm, Spinner, TextForm } from 'features/common';
import { complexValuesToOptions } from 'utils/arrays';
import { formatBytes } from 'utils/formating';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MoveDocument from './MoveDocument';
import { Modal } from 'features/common/MaterialBasedComponents';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import api from 'common/api';
import './ModalMerge.scss';

const OptionCheckbox = ({
  label,
  description,
  checkboxProps,
  defaultChecked = false,
  checked,
  disabled = false,
  className = '',
}) => (
  <div className={`merge-option merge-option--checkbox ${className}`.trim()}>
    <FormControlLabel
      control={
        <Checkbox
          {...checkboxProps}
          {...(checked !== undefined ? { checked } : { defaultChecked })}
          disabled={disabled}
        />
      }
      label={
        <div className="merge-option__text">
          <div className="merge-option__label">{label}</div>
          {description && <div className="merge-option__description">{description}</div>}
        </div>
      }
    />
  </div>
);

const parseMergeTemplateResponse = (response) => {
  const responseItems = Array.isArray(response)
    ? response
    : Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.templates)
        ? response.templates
        : [];

  return responseItems
    .filter((template) => template && template.id && template.active !== false)
    .map((template) => ({
      id: template.id,
      name: template.name || template.id,
      description: template.description || '',
      template: template.template || {},
    }));
};

export const ModalMerge = ({ show, handleClose, selectedRows, folder }) => {
  const defaultFolderId = 'auto-generated';
  const defaultFolderName = 'Merge Docs';
  const defaultFolderType = 'team-bundle';
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      folder: defaultFolderId,
      generateIndexPage: false,
      generateIndexPageOnly: false,
      generateBookMarks: true,
      generateLocalPaging: true,
      generateGlobalPaging: true,
      coreBundleUseTrialBundlePaging: false,
      useDoubleSidedPrinting: false,
      burnCrossrefsFirst: false,
      addHyperlinkToCrossRefOnMerge: false,
      addPublicHyperlinkOnMerge: false,
      globalPagingPrefix: '',
      useContainerBasedMergeOverride: false,
      fontSize: '7',
      bookmarkFormat: 'name',
      indexPageTemplateId: '',
      columnsToDisplay: [],
    },
  });
  const dispatch = useDispatch();
  const [mergingInProgress, setMergingInProgress] = useState(false);
  const [folderError, setFolderError] = useState('');
  const { transientNotifications } = useFetchNotifications();
  const [mergeNotification, setMergeNotification] = useState(null);
  const [mergeTrackingName, setMergeTrackingName] = useState('');
  const [mergeErrorMessage, setMergeErrorMessage] = useState('');
  const mergeTrackingRef = useRef({
    active: false,
    baselineCount: 0,
    lastLoggedCount: 0,
    mergedFileId: null,
    mergedFileName: null,
  });

  const currentFolder = useSelector(selectCurrentFolder);
  const dataRoomSettings = useSelector(selectCaseDataRoomSettings);
  const caseShortName = useSelector(selectCaseShortName);
  const caseClaimNo = useSelector(selectCaseClaimNo);
  const isConfidentialityRing = useSelector(selectConfidentialityRing);
  const currrentUserCR = useSelector(selectCurrentUserConfidentialityRing);
  const caseId = useSelector(selectCurrentCaseId);
  const useContainerBasedMergeGlobal = useSelector(selectUseContainerBasedMerge);
  const isInnerRing = isConfidentialityRing && currrentUserCR === 'innerRing';
  const caseState = useSelector((state) => state.case);
  const betaOptionsAvailable = useSelector(selectCanUseBetaOptions);
  const defaultBundleTitleString =
    caseShortName || caseClaimNo
      ? ` (${caseClaimNo && `${caseClaimNo}, `}${caseShortName && caseShortName})`
      : '';
  const [showHyperlinkToggle, setShowHyperlinkToggle] = useState(false);
  const [globalPagePrefix, setGlobalPagePrefix] = useState(true);
  const [globalPagePrefixVal, setGlobalPagePrefixVal] = useState(
    (folder && folder.globalPagePrefix) || '',
  );
  const [mergeTemplates, setMergeTemplates] = useState([]);
  const [fetchingMergeTemplates, setFetchingMergeTemplates] = useState(false);
  const [mergeTemplatesError, setMergeTemplatesError] = useState('');
  const [responseMsg, setResponseMsg] = useState('');
  const [mergeSummary, setMergeSummary] = useState(null);
  const dataRoomColumns = dataRoomSettings.columns || [];
  const {
    labelBetween,
    labelVersus,
    labelClaimant,
    labelDefendant,
    indexPageJurisdiction,
    indexPageClaimant,
    indexPageDefendant,
    indexPageColumnsToDisplay,
    indexPageTableDateFormat,
  } = dataRoomSettings.mergeFilesOptions || {};
  const folderToMerge = folder && folder.id;
  const defaultIndexPageColumn = useMemo(
    () =>
      indexPageColumnsToDisplay ||
      [
        'trialviewId',
        'title',
        dataRoomColumns.includes('externalId') && 'externalId',
        dataRoomColumns.includes('docDate') && 'docDate',
      ].filter(Boolean),
    [indexPageColumnsToDisplay, dataRoomColumns],
  );
  const defaultIndexPageTableDateFormat = indexPageTableDateFormat || 'dd/MM/yyyy';
  const defaultFileVariant = ['innerRing'];
  const simpleValuesToOptions = (options) =>
    options.map((value) => ({ label: moment().format(value.FE), value: value.BE }));
  const indexPageColumnsOptions = complexValuesToOptions(
    [
      { value: 'trialviewId', isFixed: false },
      { value: 'title', isFixed: false },
      { value: 'externalId', isFixed: false },
      { value: 'discoveryId', isFixed: false },
      { value: 'docDate', isFixed: false },
    ],
    'generic',
  );
  const fileVariantsOptions = complexValuesToOptions(
    [
      { value: 'innerRing', isFixed: false },
      { value: 'outerRing', isFixed: false },
      { value: 'noRing', isFixed: false },
    ],
    'generic',
  );
  const indexPageTableColumnsOptions = simpleValuesToOptions([
    { BE: 'dd/MM/yyyy HH:mm', FE: 'DD/MM/YYYY HH:mm' },
    { BE: 'dd/MM/yyyy', FE: 'DD/MM/YYYY' },
    { BE: 'dd.MM.yyyy', FE: 'DD.MM.YYYY' },
    { BE: 'dd MMM yyy', FE: 'DD MMM YYYY' },
  ]);
  const fontWeightOptions = [
    { label: 'Regular', value: 'regular' },
    { label: 'Bold', value: 'bold' },
  ];
  const bookmarkFormatOptions = [
    { label: 'Name only', value: 'name' },
    { label: 'B/T Name', value: 'btName' },
    { label: 'B/T Name Date', value: 'btNameDate' },
  ];
  const fontSizeOptions = Array.from({ length: 12 }, (_, i) => ({
    label: `${i + 5}`,
    value: `${i + 5}`,
  }));
  const footerPositionOptions = [
    { label: 'Left', value: 'BottomLeft' },
    { label: 'Center', value: 'BottomCenter' },
    { label: 'Right', value: 'BottomRight' },
    { label: 'None', value: 'None' },
  ];
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showPagingMoreOptions, setShowPagingMoreOptions] = useState(false);
  const registerCheckbox = (name, options) => {
    const { ref, ...rest } = register(name, options);
    return { inputRef: ref, ...rest };
  };

  const mergeTemplateOptions = useMemo(
    () =>
      mergeTemplates.map((template) => ({
        value: template.id,
        label: template.name || template.id,
      })),
    [mergeTemplates],
  );

  const selectedTemplateId = watch('indexPageTemplateId', '');
  const columnsToDisplayValue = watch('columnsToDisplay', defaultIndexPageColumn);
  const selectedMergeTemplate = useMemo(
    () =>
      mergeTemplates.find((template) => template.id === selectedTemplateId) ||
      mergeTemplates[0] ||
      null,
    [mergeTemplates, selectedTemplateId],
  );

  const templateInfoRows = useMemo(() => {
    const templateConfig = selectedMergeTemplate?.template || {};
    const normalizeValue = (value) => {
      if (value === null || value === undefined || value === '')
        return T.translate('case.notAvailable');
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      return `${value}`;
    };

    return [
      { label: 'ShowPageColumn', value: normalizeValue(templateConfig.ShowPageColumn) },
      { label: 'PageColumnWidth', value: normalizeValue(templateConfig.PageColumnWidth) },
      { label: 'PageColumnHeader', value: normalizeValue(templateConfig.PageColumnHeader) },
      { label: 'SerialColumnWidth', value: normalizeValue(templateConfig.SerialColumnWidth) },
      { label: 'RowsPerPage', value: normalizeValue(templateConfig.RowsPerPage) },
      { label: 'FontSize', value: normalizeValue(templateConfig.FontSize) },
    ];
  }, [selectedMergeTemplate]);

  const formatMergeStatus = (status) => {
    if (!status || typeof status !== 'string') return null;
    return status
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const formatMergeEvent = (eventType) => {
    if (!eventType || typeof eventType !== 'string') return null;
    if (eventType === 'merge.completed') {
      return T.translate('case.mergeEventCompleted');
    }
    return eventType;
  };

  const formatDuration = (durationMs) => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
    const totalSeconds = Math.round(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds || !parts.length) parts.push(`${seconds}s`);
    return parts.join(' ');
  };

  const buildMergeNotificationDetails = (details) => {
    const notAvailable = T.translate('case.notAvailable');
    const valueOrNA = (value) =>
      value === null || value === undefined || value === '' ? notAvailable : value;

    const inputParts = [];
    if (Number.isFinite(details?.inputPages)) {
      inputParts.push(`${details.inputPages} ${details.inputPages === 1 ? 'page' : 'pages'}`);
    }
    if (Number.isFinite(details?.inputBytes)) {
      inputParts.push(formatBytes(details.inputBytes, 1));
    }
    const inputValue = inputParts.length ? inputParts.join(' / ') : null;

    const mergedPagesBefore = Number.isFinite(details?.mergedPagesBefore)
      ? details.mergedPagesBefore
      : null;
    const mergedPagesAfter = Number.isFinite(details?.mergedPagesAfter)
      ? details.mergedPagesAfter
      : null;
    const mergedPagesValue =
      mergedPagesBefore !== null && mergedPagesAfter !== null
        ? `${mergedPagesBefore} -> ${mergedPagesAfter}`
        : (mergedPagesBefore ?? mergedPagesAfter ?? null);

    const statusLabel = formatMergeStatus(details?.status);
    const eventLabel = formatMergeEvent(details?.eventType);
    const durationLabel = formatDuration(details?.durationMs);
    const timestamp = details?.timestampUtc;
    const parsedTime = timestamp ? moment(timestamp) : null;
    const timestampLabel =
      parsedTime && parsedTime.isValid() ? parsedTime.format('DD/MM/YYYY HH:mm') : null;

    return [
      { label: 'Status', value: valueOrNA(statusLabel) },
      { label: 'Merged file', value: valueOrNA(details?.title) },
      { label: 'Current file', value: valueOrNA(details?.fileName) },
      { label: 'Input', value: valueOrNA(inputValue) },
      { label: 'Merged pages', value: valueOrNA(mergedPagesValue) },
      { label: 'Event', value: valueOrNA(eventLabel) },
      { label: 'Duration', value: valueOrNA(durationLabel) },
      { label: 'Last update', value: valueOrNA(timestampLabel) },
      { label: 'Error', value: valueOrNA(details?.errorMessage) },
    ];
  };

  const createDefaultFolderIfNeeded = async (selectedFolderId) => {
    if (selectedFolderId && selectedFolderId !== defaultFolderId) return selectedFolderId;

    const findFolderByName = (folders = []) => {
      for (const folderItem of folders) {
        if (!folderItem) continue;
        if (folderItem.name === defaultFolderName && !folderItem.readOnly) return folderItem;
        if (folderItem.folders && folderItem.folders.length) {
          const found = findFolderByName(folderItem.folders);
          if (found) return found;
        }
      }
      return null;
    };

    const existingFolder = findFolderByName([
      ...(caseState?.folders?.teamBundles || []),
      ...(caseState?.folders?.privateBundles || []),
      ...(caseState?.folders?.bundles || []),
      ...(caseState?.folders?.courtBundles || []),
      ...(caseState?.folders?.trialbooks || []),
    ]);

    if (existingFolder && existingFolder.id) return existingFolder.id;

    const folderPayload = {
      name: defaultFolderName,
      type: defaultFolderType,
    };

    const addFolderResponse = await dispatch(
      actions.addFolder(folderPayload, { suppressFeedback: true }),
    );

    if (addFolderResponse && addFolderResponse.id) {
      try {
        await dispatch(actions.fetchContainer({ folder: { type: defaultFolderType } }));
      } catch {
        // Proceed even if the sidebar refresh fails
      }
      return addFolderResponse.id;
    }

    throw new Error('Failed to create merge folder');
  };

  const includeCrossRefs = watch('burnCrossrefsFirst', false);
  const generateIndexPageValue = watch('generateIndexPage', false);
  const generateIndexPageOnlyValue = watch('generateIndexPageOnly', false);
  const generateBookMarksValue = watch('generateBookMarks', true);
  const generateLocalPagingValue = watch('generateLocalPaging', true);
  const generateGlobalPagingValue = watch('generateGlobalPaging', true);
  const coreBundleUseTrialBundlePagingValue = watch('coreBundleUseTrialBundlePaging', false);
  const useDoubleSidedPrintingValue = watch('useDoubleSidedPrinting', false);
  const addHyperlinkToCrossRefOnMergeValue = watch('addHyperlinkToCrossRefOnMerge', false);
  const addPublicHyperlinkOnMergeValue = watch('addPublicHyperlinkOnMerge', false);
  const convertPublicHyperlinksToInternalOnMergeValue = watch('convertPublicHyperlinksToInternalOnMerge', false);
  const placeholderForAudioVideoValue = watch('placeholderForAudioVideo', false);
  const placeholderForExcelValue = watch('placeholderForExcel', false);
  const placeholderForUnconvertedValue = watch('placeholderForUnconverted', false);
  const useContainerBasedMergeOverrideValue = watch('useContainerBasedMergeOverride', false);
  const useContainerBasedMerge =
    useContainerBasedMergeOverrideValue || useContainerBasedMergeGlobal;
  const footerBtpValue = watch('footerBtp', 'BottomLeft');
  const footerGpValue = watch('footerGp', 'BottomRight');
  const footerIdValue = watch('footerId', 'BottomCenter');
  const hasFooterPositionConflict = useMemo(() => {
    const positions = [footerBtpValue, footerGpValue, footerIdValue].filter(
      (v) => v && v !== 'None',
    );
    return positions.length !== new Set(positions).size;
  }, [footerBtpValue, footerGpValue, footerIdValue]);
  const shouldShowIndexPageDateFormat =
    generateIndexPageValue &&
    (useContainerBasedMerge ||
      (Array.isArray(columnsToDisplayValue) && columnsToDisplayValue.includes('docDate')));
  const submitMerge = handleSubmit(async (props) => {
    const {
      folder: selectedFolderId,
      name: mergedFileName,
      mergeFileVariants,
      generateIndexPage = false,
      generateIndexPageOnly = false,
      generateBookMarks = true,
      generateLocalPaging = true,
      generateGlobalPaging = true,
      coreBundleUseTrialBundlePaging = false,
      useDoubleSidedPrinting = false,
      burnCrossrefsFirst = false,
      addHyperlinkToCrossRefOnMerge = false,
      addPublicHyperlinkOnMerge = false,
      convertPublicHyperlinksToInternalOnMerge = false,
      placeholderForAudioVideo = false,
      placeholderForExcel = false,
      placeholderForUnconverted = false,
      globalPagingPrefix = '',
      indexPageTableBorder,
      indexPageClaimNo,
      indexPageJurisdiction,
      indexPageClaimant,
      indexPageDefendant,
      indexPageBundleTitle,
      indexPageTemplateId,
      columnsToDisplay,
      indexPageDateFormat,
      fontWeight,
      fontSize,
      bookmarkFormat,
      footerBtp = 'BottomLeft',
      footerGp = 'BottomRight',
      footerId = 'BottomCenter',
      useContainerBasedMergeOverride = false,
    } = props;
    if (!selectedFolderId || selectedFolderId === '') {
      setFolderError(T.translate('case.selectFolderToCopy'));
      return;
    }
    setFolderError('');
    setMergingInProgress(true);
    setMergeErrorMessage('');
    if (useContainerBasedMergeOverride || useContainerBasedMergeGlobal) {
      setMergeTrackingName(mergedFileName || '');
      const baselineCount = Array.isArray(transientNotifications)
        ? transientNotifications.length
        : 0;
      mergeTrackingRef.current = {
        active: true,
        baselineCount,
        lastLoggedCount: baselineCount,
        mergedFileId: null,
        mergedFileName: mergedFileName || null,
      };
      setMergeNotification(null);
    } else {
      mergeTrackingRef.current.active = false;
      setMergeTrackingName('');
      setMergeNotification(null);
    }

    let folderId;
    try {
      folderId = await createDefaultFolderIfNeeded(selectedFolderId);
    } catch {
      setMergingInProgress(false);
      setResponseMsg(T.translate('generic.errorOccurred'));
      return;
    }

    dispatch(
      actions.mergeDocuments({
        mergedFileName,
        mergeFileVariants,
        generateIndexPage,
        generateIndexPageOnly,
        generateBookMarks,
        generateLocalPaging,
        generateGlobalPaging,
        globalPagingPrefix,
        ...(selectedRows && {
          filesToMerge: selectedRows.map(({ id, name }) => ({ id, name })),
        }),
        folderId,
        labelBetween,
        labelVersus,
        labelClaimant,
        labelDefendant,
        indexPageTableBorder,
        indexPageClaimNo,
        indexPageJurisdiction,
        indexPageClaimant,
        indexPageDefendant,
        indexPageBundleTitle,
        ...(useContainerBasedMerge
          ? indexPageTemplateId
            ? { indexPageTemplateId }
            : {}
          : { columnsToDisplay }),
        indexPageDateFormat,
        fontWeight,
        fontSize: Number(fontSize),
        bookmarkFormat,
        footerConfig: {
          Btp: {
            Show: footerBtp !== 'None',
            Position: footerBtp === 'None' ? 'BottomLeft' : footerBtp,
          },
          Id: {
            Show: footerId !== 'None',
            Position: footerId === 'None' ? 'BottomCenter' : footerId,
          },
          Gp: {
            Show: footerGp !== 'None',
            Position: footerGp === 'None' ? 'BottomRight' : footerGp,
          },
        },
        folderToMerge,
        coreBundleUseTrialBundlePaging,
        useDoubleSidedPrinting,
        burnCrossrefsFirst,
        addHyperlinkToCrossRefOnMerge,
        addPublicHyperlinkOnMerge,
        ...(addPublicHyperlinkOnMerge && convertPublicHyperlinksToInternalOnMerge
          ? { convertPublicHyperlinksToInternalOnMerge: true }
          : {}),
        placeholderForAudioVideo,
        placeholderForExcel,
        placeholderForUnconverted,
        useContainerBasedMergeOverride,
        suppressFeedback: true,
      }),
    )
      .then((res) => {
        setMergingInProgress(false);
        const summary = res?.resMerge;

        if (useContainerBasedMergeOverride || useContainerBasedMergeGlobal) {
          if (summary) {
            mergeTrackingRef.current.mergedFileId =
              summary?.mergedFileId || summary?.id || summary?.mergedFile?.id || null;
          }
          if (summary) {
            setMergeSummary(summary);
            setResponseMsg('');
          } else {
            setResponseMsg(T.translate('case.mergeAsyncMsg'));
          }
          return;
        }

        if (summary?.asyncMerge) {
          setResponseMsg(T.translate('case.mergeAsyncMsg'));
        } else {
          handleClose();
        }
      })
      .catch((rejection) => {
        setMergingInProgress(false);
        const rawError =
          rejection?.resMerge?.error ?? rejection?.res?.error ?? rejection?.body ?? '';
        const backendMessage =
          typeof rawError === 'string' ? rawError : rawError?.message || rawError?.body || '';
        setMergeErrorMessage(backendMessage || '');
        setResponseMsg(
          backendMessage
            ? `${T.translate('generic.errorOccurred')}: ${backendMessage}`
            : T.translate('generic.errorOccurred'),
        );
      });
  });

  const wizardSteps = [
    {
      key: 'destination',
      title: 'Destination',
      subtitle: 'Folder & name',
      panelTitle: 'Choose where the merged PDF will live',
      panelDescription: T.translate('case.mergeDestinationDescription'),
      content: (
        <>
          <div className="merge-grid merge-grid--single">
            <MoveDocument
              control={control}
              errors={errors}
              canUnselect={false}
              readOnly={mergingInProgress}
              currentFolderType={folder ? folder.type : currentFolder.type || null}
              folderError={folderError}
              menuPlacement="bottom"
              defaultFolderName={defaultFolderName}
              excludePublicContainers={true}
            />
          </div>
          <div className="merge-grid merge-grid--single">
            <TextForm
              {...register('name')}
              label={T.translate('case.mergeFileName')}
              placeholder={T.translate('case.mergeFileNamePlaceholder')}
              defaultValue={`${
                (selectedRows
                  ? currentFolder.name || T.translate('case.allDocuments')
                  : folder.name || T.translate('case.allDocuments')) + defaultBundleTitleString
              } (${moment().format('DD/MM/YYYY')})`}
              readOnly={mergingInProgress}
              autofocus
            />
          </div>
          {isInnerRing && (
            <div className="merge-grid merge-grid--single">
              <MultiSelectForm
                label={T.translate('case.selectFileVariants')}
                name="mergeFileVariants"
                defaultValue={defaultFileVariant}
                options={fileVariantsOptions}
                control={control}
                errors={errors}
                rules={{ required: true }}
              />
            </div>
          )}
          {betaOptionsAvailable && (
            <OptionCheckbox
              label="Use Container Merge"
              description="Use container-based merging pipeline for this job."
              checkboxProps={registerCheckbox('useContainerBasedMergeOverride')}
              checked={useContainerBasedMergeOverrideValue}
              disabled={mergingInProgress}
            />
          )}
        </>
      ),
    },
    {
      key: 'indexing',
      title: 'Indexing',
      subtitle: 'Cover & index',
      panelTitle: 'Create a cover and index',
      panelDescription: T.translate('case.mergeIndexingDescription'),
      content: (
        <>
          <OptionCheckbox
            label={T.translate(`case.generateIndexPage`)}
            description={T.translate('case.generateIndexPageDescription')}
            checkboxProps={registerCheckbox('generateIndexPage', {})}
            checked={generateIndexPageValue}
            disabled={mergingInProgress}
          />

          {generateIndexPageValue && (
            <div className="merge-nested">
              <OptionCheckbox
                label={T.translate(`case.generateIndexPageOnly`)}
                description={T.translate('case.generateIndexPageOnlyDescription')}
                checkboxProps={registerCheckbox('generateIndexPageOnly')}
                checked={generateIndexPageOnlyValue}
                disabled={mergingInProgress}
              />

              <div className="">
                <TextForm
                  {...register('indexPageBundleTitle')}
                  label={T.translate('case.indexPageBundleTitle')}
                  placeholder={T.translate('case.indexPageBundleTitlePlaceholder')}
                  defaultValue={
                    selectedRows
                      ? currentFolder.name || T.translate('case.allDocuments')
                      : folder.name
                  }
                  readOnly={mergingInProgress}
                />
                {useContainerBasedMerge ? (
                  <div className="merge-template-field">
                    <SelectForm
                      label={
                        <div className="merge-template-label">
                          <span>{T.translate('generic.template')}</span>
                          <Tooltip
                            arrow
                            title={
                              <div className="merge-template-tooltip">
                                {templateInfoRows.map(({ label, value }) => (
                                  <div className="merge-template-tooltip__row" key={label}>
                                    <span className="merge-template-tooltip__label">{label}</span>
                                    <span className="merge-template-tooltip__value">{value}</span>
                                  </div>
                                ))}
                              </div>
                            }
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              className="merge-template-label__info"
                              disabled={!selectedMergeTemplate}
                            >
                              <InfoOutlined fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      }
                      name="indexPageTemplateId"
                      options={mergeTemplateOptions}
                      defaultValue={selectedTemplateId || ''}
                      control={control}
                      errors={errors}
                      rules={{ required: generateIndexPageValue }}
                      readOnly={
                        mergingInProgress || fetchingMergeTemplates || !mergeTemplateOptions.length
                      }
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                    />
                    {selectedMergeTemplate?.description ? (
                      <div className="merge-template-field__description">
                        {selectedMergeTemplate.description}
                      </div>
                    ) : null}
                    {mergeTemplatesError ? (
                      <div className="merge-template-field__error">{mergeTemplatesError}</div>
                    ) : null}
                  </div>
                ) : (
                  <MultiSelectForm
                    label={T.translate('case.heading4')}
                    name="columnsToDisplay"
                    defaultValue={defaultIndexPageColumn}
                    options={indexPageColumnsOptions}
                    control={control}
                    error={errors}
                  />
                )}
              </div>

              {shouldShowIndexPageDateFormat && (
                <div className="merge-grid merge-grid--single">
                  <SelectForm
                    label={T.translate('case.IndexPageTableDateFormat')}
                    name="indexPageDateFormat"
                    defaultValue={defaultIndexPageTableDateFormat}
                    options={indexPageTableColumnsOptions}
                    control={control}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              )}

              <div
                className="merge-more-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowMoreOptions(!showMoreOptions)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setShowMoreOptions(!showMoreOptions);
                }}
              >
                <span>{T.translate('case.moreOptions')}</span>
                {showMoreOptions ? (
                  <KeyboardArrowUp fontSize="small" />
                ) : (
                  <KeyboardArrowDown fontSize="small" />
                )}
              </div>

              {showMoreOptions && (
                <div className="merge-more-content">
                  {useContainerBasedMerge && (
                    <div className="merge-grid merge-grid--single">
                      <SelectForm
                        label={T.translate('case.IndexPageFontWeight')}
                        name="fontWeight"
                        defaultValue="regular"
                        options={fontWeightOptions}
                        control={control}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                  )}

                  {useContainerBasedMerge && (
                    <div className="merge-grid merge-grid--single">
                      <SelectForm
                        label={T.translate('case.IndexPageFontSize')}
                        name="fontSize"
                        defaultValue="7"
                        options={fontSizeOptions}
                        control={control}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                  )}

                  <TextForm
                    {...register('indexPageClaimNo')}
                    as="textarea"
                    label={T.translate('case.indexPageClaimNo')}
                    placeholder={T.translate('case.indexPageClaimNoPlaceholder')}
                    defaultValue={caseClaimNo}
                    readOnly={mergingInProgress}
                  />
                  <TextForm
                    {...register('indexPageJurisdiction')}
                    as="textarea"
                    label={T.translate('case.indexPageJurisdiction')}
                    placeholder={T.translate('case.indexPageJurisdictionPlaceholder')}
                    defaultValue={indexPageJurisdiction}
                    readOnly={mergingInProgress}
                  />
                  <TextForm
                    {...register('indexPageClaimant')}
                    as="textarea"
                    label={labelClaimant || T.translate('case.indexPageClaimant')}
                    placeholder={T.translate('case.indexPageClaimantPlaceholder')}
                    defaultValue={indexPageClaimant}
                    readOnly={mergingInProgress}
                  />
                  <TextForm
                    {...register('indexPageDefendant')}
                    as="textarea"
                    label={labelDefendant || T.translate('case.indexPageDefendant')}
                    placeholder={T.translate('case.indexPageDefendantPlaceholder')}
                    defaultValue={indexPageDefendant}
                    readOnly={mergingInProgress}
                  />
                </div>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'paging',
      title: 'Paging',
      subtitle: 'Bookmarks & numbers',
      panelTitle: 'Paging and navigation',
      panelDescription: T.translate('case.mergePagingDescription'),
      content: (
        <>
          <OptionCheckbox
            label={T.translate(`case.generateBookMarks`)}
            description={T.translate('case.generateBookMarksDescription')}
            checkboxProps={registerCheckbox('generateBookMarks')}
            checked={generateBookMarksValue}
            disabled={mergingInProgress}
            className={
              useContainerBasedMerge && generateBookMarksValue
                ? 'merge-option--no-pad-bottom'
                : ''
            }
          />

          {useContainerBasedMerge && generateBookMarksValue && (
            <div className="merge-nested merge-nested--tight">
              <div className="merge-grid merge-grid--single">
                <SelectForm
                  label={T.translate('case.IndexPageBookmarkFormat')}
                  name="bookmarkFormat"
                  defaultValue="name"
                  options={bookmarkFormatOptions}
                  control={control}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </div>
          )}

          {!useContainerBasedMerge && (
            <>
              <OptionCheckbox
                label={T.translate(`case.generateLocalPaging`)}
                description={T.translate('case.generateLocalPagingDescription')}
                checkboxProps={registerCheckbox('generateLocalPaging')}
                checked={generateLocalPagingValue}
                disabled={mergingInProgress}
              />

              <div className="merge-option merge-option--checkbox merge-option--inline-input">
                <FormControlLabel
                  control={
                    <Checkbox
                      {...registerCheckbox('generateGlobalPaging', {
                        onChange: (e) => setGlobalPagePrefix(e.target.checked),
                      })}
                      checked={generateGlobalPagingValue}
                      disabled={mergingInProgress}
                    />
                  }
                  label={
                    <div className="merge-option__text">
                      <div className="merge-option__label">
                        {T.translate(`case.generateGlobalPaging`)}
                      </div>
                      <div className="merge-option__description">
                        {T.translate('case.generateGlobalPagingDescription')}
                      </div>
                    </div>
                  }
                />
                {globalPagePrefix && (
                  <div className="merge-option__control">
                    <TextForm
                      {...register('globalPagingPrefix', {
                        onChange: (e) => {
                          e.target.value.length > 1
                            ? setGlobalPagePrefixVal(e.target.value.slice(0, 1))
                            : setGlobalPagePrefixVal(e.target.value);
                        },
                      })}
                      placeholder={T.translate('case.globalPagePrefixPlaceholder')}
                      value={globalPagePrefixVal}
                      readOnly={mergingInProgress}
                      className="merge-option__inline-field"
                      style={{ textAlign: 'center' }}
                    />
                  </div>
                )}
              </div>
            </>
          )}
          <OptionCheckbox
            label={T.translate(`case.coreBundleUseTrialBundlePaging`)}
            description={T.translate('case.coreBundleUseTrialBundlePagingDescription')}
            checkboxProps={registerCheckbox('coreBundleUseTrialBundlePaging')}
            checked={coreBundleUseTrialBundlePagingValue}
            disabled={mergingInProgress}
          />

          <div
            className="merge-more-toggle"
            role="button"
            tabIndex={0}
            onClick={() => setShowPagingMoreOptions(!showPagingMoreOptions)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                setShowPagingMoreOptions(!showPagingMoreOptions);
            }}
          >
            <span>{T.translate('case.moreOptions')}</span>
            {showPagingMoreOptions ? (
              <KeyboardArrowUp fontSize="small" />
            ) : (
              <KeyboardArrowDown fontSize="small" />
            )}
          </div>

          {showPagingMoreOptions && (
            <div className="merge-more-content">
              {useContainerBasedMerge && (
                <>
                  <div className="merge-option">
                    <div className="merge-option__text">
                      <div className="merge-option__label">
                        {T.translate('case.FooterElementsPlacement')}
                      </div>
                    </div>
                  </div>
                  <div className="merge-grid merge-grid--three merge-footer-fields">
                    <div>
                      <SelectForm
                        label={T.translate('case.FooterBtp')}
                        name="footerBtp"
                        defaultValue="BottomLeft"
                        options={footerPositionOptions}
                        control={control}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                    <div>
                      <SelectForm
                        label={T.translate('case.FooterId')}
                        name="footerId"
                        defaultValue="BottomCenter"
                        options={footerPositionOptions}
                        control={control}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                    <div>
                      <SelectForm
                        label={T.translate('case.FooterGp')}
                        name="footerGp"
                        defaultValue="BottomRight"
                        options={footerPositionOptions}
                        control={control}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                  {hasFooterPositionConflict && (
                    <div className="merge-footer-error">
                      {T.translate('case.FooterPositionConflict')}
                    </div>
                  )}
                </>
              )}

              <OptionCheckbox
                label={T.translate('case.placeholderForAudioVideo')}
                description={T.translate('case.placeholderForAudioVideoDescription')}
                checkboxProps={registerCheckbox('placeholderForAudioVideo')}
                checked={placeholderForAudioVideoValue}
                disabled={mergingInProgress}
              />

              {useContainerBasedMerge && (
                <>
                  <OptionCheckbox
                    label={T.translate('case.placeholderForExcel')}
                    description={T.translate('case.placeholderForExcelDescription')}
                    checkboxProps={registerCheckbox('placeholderForExcel')}
                    checked={placeholderForExcelValue}
                    disabled={mergingInProgress}
                  />

                  <OptionCheckbox
                    label={T.translate('case.placeholderForUnconverted')}
                    description={T.translate('case.placeholderForUnconvertedDescription')}
                    checkboxProps={registerCheckbox('placeholderForUnconverted')}
                    checked={placeholderForUnconvertedValue}
                    disabled={mergingInProgress}
                  />
                </>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'finishing',
      title: 'Finishing',
      subtitle: 'Cross refs & print',
      panelTitle: 'Cross references and finishing touches',
      panelDescription: T.translate('case.mergeFinishingDescription'),
      content: (
        <>
          <OptionCheckbox
            label={T.translate(`case.useDoubleSidedPrinting`)}
            description={T.translate('case.useDoubleSidedPrintingDescription')}
            checkboxProps={registerCheckbox('useDoubleSidedPrinting')}
            checked={useDoubleSidedPrintingValue}
            disabled={mergingInProgress}
          />

          <OptionCheckbox
            label={T.translate(`case.includeCrossRef`)}
            description={T.translate('case.includeCrossRefDescription')}
            checkboxProps={registerCheckbox('burnCrossrefsFirst', {
              onChange: (e) => setShowHyperlinkToggle(e.target.checked),
            })}
            checked={includeCrossRefs}
            disabled={mergingInProgress}
          />

          {showHyperlinkToggle && (
            <OptionCheckbox
              label={T.translate(`case.convertCrossRefToExternalHyperlink`)}
              description={T.translate('case.convertCrossRefToExternalHyperlinkDescription')}
              checkboxProps={registerCheckbox('addHyperlinkToCrossRefOnMerge')}
              checked={addHyperlinkToCrossRefOnMergeValue}
              disabled={mergingInProgress}
            />
          )}

          {useContainerBasedMerge && (
            <>
              <OptionCheckbox
                label={T.translate(`case.includePublicHyperLinks`)}
                description={T.translate('case.includePublicHyperLinksOnMergeDescription')}
                checkboxProps={registerCheckbox('addPublicHyperlinkOnMerge')}
                checked={addPublicHyperlinkOnMergeValue}
                disabled={mergingInProgress}
              />

              {addPublicHyperlinkOnMergeValue && (
                <OptionCheckbox
                  label={T.translate(`case.convertPublicHyperlinksToInternal`)}
                  description={T.translate('case.convertPublicHyperlinksToInternalDescription')}
                  checkboxProps={registerCheckbox('convertPublicHyperlinksToInternalOnMerge')}
                  checked={convertPublicHyperlinksToInternalOnMergeValue}
                  disabled={mergingInProgress}
                />
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const stepFieldRequirements = {
    0: ['folder'],
  };
  const isLastStep = activeStep === wizardSteps.length - 1;
  const progressAmount =
    wizardSteps.length > 1 ? (activeStep / (wizardSteps.length - 1)) * 100 : 100;
  const primaryActionLabel = isLastStep ? (
    mergingInProgress ? (
      <Spinner style={{ height: '1.5rem' }} />
    ) : (
      T.translate('generic.create')
    )
  ) : (
    T.translate('generic.next')
  );
  const hasAsyncStatus = !!responseMsg || !!mergeSummary || !!mergeNotification;
  const isMergeError = !!mergeErrorMessage;
  const canNavigateSteps = !mergingInProgress && !hasAsyncStatus;
  const shouldShowMergeDetails = useContainerBasedMerge && hasAsyncStatus && !isMergeError;
  const mergeDetailSource = mergeNotification
    ? {
        status: mergeNotification.status,
        title: mergeNotification.title,
        fileName: mergeNotification.data?.fileName,
        inputPages: mergeNotification.data?.inputPages,
        inputBytes: mergeNotification.data?.inputBytes,
        mergedPagesBefore: mergeNotification.data?.mergedPagesBefore,
        mergedPagesAfter: mergeNotification.data?.mergedPagesAfter,
        eventType: mergeNotification.data?.eventType,
        durationMs: mergeNotification.data?.durationMs,
        timestampUtc: mergeNotification.data?.timestampUtc || mergeNotification.createdBy?.date,
        errorMessage: mergeNotification.data?.errorMessage,
      }
    : mergeSummary
      ? {
          status: mergeSummary.status,
          title: mergeSummary.title || mergeTrackingName || null,
          mergedPagesAfter: Number.isFinite(mergeSummary.pageCount) ? mergeSummary.pageCount : null,
          timestampUtc: mergeSummary.timestampUtc || mergeSummary.updatedAt || null,
        }
      : {
          title: mergeTrackingName || null,
        };
  const mergeStatusLabel =
    formatMergeStatus(mergeDetailSource?.status) || T.translate('case.mergeSummaryPending');
  const mergeNotificationDetails = shouldShowMergeDetails
    ? buildMergeNotificationDetails(mergeDetailSource)
    : [];

  useEffect(() => {
    if (!show || !useContainerBasedMerge) return;
    let isActive = true;

    const fetchMergeTemplates = async () => {
      setFetchingMergeTemplates(true);
      setMergeTemplatesError('');
      try {
        const response = await api.get(`/cases/${caseId}/merge-templates`);
        if (response?.error) throw new Error(response.error);
        const parsedTemplates = parseMergeTemplateResponse(response);
        if (!isActive) return;
        setMergeTemplates(parsedTemplates);
        if (!parsedTemplates.length) {
          setMergeTemplatesError('No merge templates are available.');
        }
      } catch {
        if (!isActive) return;
        setMergeTemplates([]);
        setMergeTemplatesError('Failed to load merge templates.');
      } finally {
        if (isActive) {
          setFetchingMergeTemplates(false);
        }
      }
    };

    fetchMergeTemplates();

    return () => {
      isActive = false;
    };
  }, [show, useContainerBasedMerge, caseId]);

  useEffect(() => {
    if (!show || !useContainerBasedMerge) return;
    if (!mergeTemplateOptions.length) {
      setValue('indexPageTemplateId', '');
      return;
    }

    const selectedTemplateStillExists = mergeTemplateOptions.some(
      (option) => option.value === selectedTemplateId,
    );

    if (!selectedTemplateStillExists) {
      setValue('indexPageTemplateId', mergeTemplateOptions[0].value, {
        shouldValidate: true,
      });
    }
  }, [show, useContainerBasedMerge, mergeTemplateOptions, selectedTemplateId, setValue]);

  useEffect(() => {
    const notificationList = Array.isArray(transientNotifications) ? transientNotifications : [];
    if (!show || !useContainerBasedMerge || !mergeTrackingRef.current.active) {
      mergeTrackingRef.current.lastLoggedCount = notificationList.length;
      return;
    }

    if (!notificationList.length) return;

    const previousCount = mergeTrackingRef.current.lastLoggedCount;
    if (notificationList.length < previousCount) {
      mergeTrackingRef.current.lastLoggedCount = notificationList.length;
      return;
    }
    if (notificationList.length <= previousCount) return;

    notificationList.slice(previousCount).forEach((notification) => {
      console.log('[ModalMerge] transient notification received', notification);
    });
    mergeTrackingRef.current.lastLoggedCount = notificationList.length;
  }, [show, useContainerBasedMerge, transientNotifications]);

  useEffect(() => {
    if (!show || !useContainerBasedMerge || !mergeTrackingRef.current.active) return;
    if (!Array.isArray(transientNotifications) || !transientNotifications.length) return;

    const tracking = mergeTrackingRef.current;
    const notifications = transientNotifications;

    if (notifications.length < tracking.baselineCount) {
      tracking.baselineCount = notifications.length;
    }

    let candidateNotifications = notifications.slice(tracking.baselineCount);
    candidateNotifications = candidateNotifications.filter(
      (notification) => notification?.transient && notification?.type === 'merge',
    );

    if (tracking.mergedFileId) {
      candidateNotifications = candidateNotifications.filter(
        (notification) =>
          notification?.additionalId === tracking.mergedFileId ||
          notification?.data?.mergedFileId === tracking.mergedFileId,
      );
    } else if (tracking.mergedFileName) {
      candidateNotifications = candidateNotifications.filter(
        (notification) => notification?.title === tracking.mergedFileName,
      );
    }

    if (!candidateNotifications.length) return;

    const getNotificationTimestamp = (notification) => {
      const timestamp =
        notification?.data?.timestampUtc ||
        notification?.createdBy?.date ||
        notification?.timestampUtc;
      if (!timestamp) return 0;
      const parsed = Date.parse(timestamp);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const latestNotification = candidateNotifications.reduce((latest, current) => {
      return getNotificationTimestamp(current) >= getNotificationTimestamp(latest)
        ? current
        : latest;
    }, candidateNotifications[0]);

    setMergeNotification(latestNotification);
  }, [show, useContainerBasedMerge, transientNotifications]);

  useEffect(() => {
    if (!show) return;
    const defaultMergedName = `${
      (selectedRows
        ? currentFolder.name || T.translate('case.allDocuments')
        : folder.name || T.translate('case.allDocuments')) + defaultBundleTitleString
    } (${moment().format('DD/MM/YYYY')})`;

    reset({
      folder: defaultFolderId,
      name: defaultMergedName,
      mergeFileVariants: isInnerRing ? defaultFileVariant : undefined,
      generateIndexPage: false,
      generateIndexPageOnly: false,
      generateBookMarks: true,
      generateLocalPaging: true,
      generateGlobalPaging: true,
      coreBundleUseTrialBundlePaging: false,
      useDoubleSidedPrinting: false,
      burnCrossrefsFirst: false,
      addHyperlinkToCrossRefOnMerge: false,
      addPublicHyperlinkOnMerge: false,
      placeholderForAudioVideo: false,
      placeholderForExcel: false,
      placeholderForUnconverted: false,
      globalPagingPrefix: (folder && folder.globalPagePrefix) || '',
      useContainerBasedMergeOverride: false,
      indexPageBundleTitle: selectedRows
        ? currentFolder.name || T.translate('case.allDocuments')
        : folder.name,
      indexPageTemplateId: '',
      columnsToDisplay: defaultIndexPageColumn,
      indexPageDateFormat: defaultIndexPageTableDateFormat,
      fontWeight: 'regular',
      fontSize: '7',
      bookmarkFormat: 'name',
      footerBtp: 'BottomLeft',
      footerGp: 'BottomRight',
      footerId: 'BottomCenter',
    });

    setActiveStep(0);
    setResponseMsg('');
    setMergeSummary(null);
    setMergeNotification(null);
    setMergeErrorMessage('');
    setMergeTrackingName('');
    mergeTrackingRef.current = {
      active: false,
      baselineCount: 0,
      lastLoggedCount: 0,
      mergedFileId: null,
      mergedFileName: null,
    };
    setFolderError('');
    setShowHyperlinkToggle(false);
    setShowMoreOptions(false);
    setGlobalPagePrefix(true);
    setGlobalPagePrefixVal((folder && folder.globalPagePrefix) || '');
  }, [
    show,
    selectedRows,
    folder?.id,
    currentFolder.name,
    defaultBundleTitleString,
    isInnerRing,
    defaultIndexPageTableDateFormat,
    reset,
    defaultFolderId,
  ]);

  useEffect(() => {
    setShowHyperlinkToggle(!!includeCrossRefs);
  }, [includeCrossRefs]);

  const validateStep = async (stepIndex) => {
    const fields = stepFieldRequirements[stepIndex] || [];
    if (!fields.length) return true;
    const isValid = await trigger(fields);
    if (!isValid && fields.includes('folder')) {
      setFolderError(T.translate('case.selectFolderToCopy'));
    } else {
      setFolderError('');
    }
    return isValid;
  };

  const attemptStepChange = async (targetStep) => {
    if (targetStep === activeStep) return;
    if (targetStep > activeStep) {
      const ok = await validateStep(activeStep);
      if (!ok) return;
    }
    setActiveStep(Math.max(0, Math.min(targetStep, wizardSteps.length - 1)));
  };

  const handlePrimaryAction = async () => {
    if (mergingInProgress) return;
    if (!isLastStep) {
      await attemptStepChange(activeStep + 1);
      return;
    }
    submitMerge();
  };

  const renderStepper = () => (
    <div className="merge-wizard__stepper">
      <div className="merge-wizard__progress">
        <div className="merge-wizard__progress-track" />
        <div className="merge-wizard__progress-active" style={{ width: `${progressAmount}%` }} />
      </div>
      {wizardSteps.map((step, index) => {
        const isActive = index === activeStep;
        const isComplete = index < activeStep;
        return (
          <button
            key={step.key}
            type="button"
            disabled={!canNavigateSteps}
            onClick={() => {
              if (!canNavigateSteps) return;
              attemptStepChange(index);
            }}
            className={`merge-step ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''} ${
              !canNavigateSteps ? 'is-disabled' : ''
            }`}
          >
            <div className="merge-step__circle">
              {isComplete ? <Check fontSize="small" /> : index + 1}
            </div>
            <div className="merge-step__text">
              <div className="merge-step__title">{step.title}</div>
              <div className="merge-step__subtitle">{step.subtitle}</div>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <Modal
      className={`merge-modal-dialog ${
        mergeSummary || mergeNotification ? 'merge-modal-dialog--compact' : ''
      }`}
      show={show}
      title={selectedRows ? T.translate('case.merge') : T.translate('case.mergeTrialbook')}
      handleClose={handleClose}
      handleSave={handlePrimaryAction}
      saveTitle={primaryActionLabel}
      disableSave={mergingInProgress || hasAsyncStatus || hasFooterPositionConflict}
      buttons={
        activeStep > 0 || isMergeError ? (
          <Button
            onClick={() => {
              if (isMergeError) {
                setMergeErrorMessage('');
                setResponseMsg('');
                setMergeSummary(null);
                setMergeNotification(null);
                setMergeTrackingName('');
                mergeTrackingRef.current.active = false;
              } else if (activeStep > 0) {
                setActiveStep((prev) => Math.max(prev - 1, 0));
              }
            }}
            variant="outlined"
            color="primary"
            disabled={mergingInProgress || (hasAsyncStatus && !isMergeError)}
          >
            {T.translate('generic.back')}
          </Button>
        ) : null
      }
    >
      <PerfectScrollbar className="merge-scroll" options={{ suppressScrollX: true }}>
        {hasAsyncStatus ? (
          isMergeError ? (
            <div className="merge-modal merge-modal--message">
              <div className="merge-message merge-status merge-status--error">
                <div className="merge-status__icon">
                  <ErrorOutline fontSize="inherit" />
                </div>
                <div className="merge-message__text merge-message__text--error">
                  {mergeErrorMessage || T.translate('generic.errorOccurred')}
                </div>
              </div>
            </div>
          ) : (
            <div className="merge-modal merge-modal--message">
              <div className="merge-message merge-status">
                <div className="merge-status__icon">
                  <Check fontSize="inherit" />
                </div>
                <div className="merge-message__title">
                  {mergeStatusLabel.toLowerCase() === 'complete'
                    ? T.translate('case.mergeContainerCompletedTitle')
                    : mergeNotification || mergeSummary || useContainerBasedMerge
                      ? T.translate('case.mergeContainerQueuedTitle')
                      : T.translate('case.merge')}
                </div>
                <div className="merge-message__text merge-message__text--progress">
                  {mergeStatusLabel.toLowerCase() !== 'complete' && (
                    <CircularProgress size={18} sx={{ color: '#4667e5' }} />
                  )}
                  <span>
                    {mergeNotification?.message ||
                      responseMsg ||
                      T.translate('case.mergeInProgressMessage')}
                  </span>
                </div>
                {mergeStatusLabel.toLowerCase() !== 'created' && (
                  <div className="merge-status__pill">
                    <span
                      className={`merge-status__dot ${
                        mergeStatusLabel.toLowerCase() === 'complete' ? 'is-complete' : ''
                      }`}
                    />
                    {mergeStatusLabel}
                  </div>
                )}
                {shouldShowMergeDetails && !!mergeNotificationDetails.length && (
                  <div className="merge-summary merge-summary--card">
                    <div className="merge-summary__list">
                      {mergeNotificationDetails.map(({ label, value }) => (
                        <div className="merge-summary__item" key={label}>
                          <div className="merge-summary__label">{label}</div>
                          <div className="merge-summary__value">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          <div className="merge-modal">
            <div className="merge-wizard">
              <div className="merge-intro">
                {selectedRows
                  ? T.translate('case.mergeTitle', {
                      numberOfFiles: selectedRows.length,
                      folderName: currentFolder.name || T.translate('case.allDocuments'),
                    })
                  : T.translate('case.mergeTrialbookMsg', { folderName: folder.name })}
              </div>

              {renderStepper()}

              <div className="merge-panel">
                <div className="merge-panel__head">
                  <div className="merge-panel__eyebrow">
                    Step {activeStep + 1} of {wizardSteps.length}
                  </div>
                  <div className="merge-panel__title">{wizardSteps[activeStep].panelTitle}</div>
                  <div className="merge-panel__subtitle">
                    {wizardSteps[activeStep].panelDescription}
                  </div>
                </div>
                <div className="merge-panel__body">{wizardSteps[activeStep].content}</div>
              </div>
            </div>
          </div>
        )}
      </PerfectScrollbar>
    </Modal>
  );
};

export default ModalMerge;
